// ============================================================================
// FloatingChat.js — Messagerie chat entre utilisateurs
//
// Widget flottant en bas-droite (en miroir du bouton Socrate à 70px du bas),
// inspiré de Messenger / Slack mobile.
//
// Fonctionnalités MVP :
//   • Liste des conversations (1-to-1 et groupes) triée par dernière activité
//   • Thread d'une conversation avec envoi de messages en temps réel
//   • Création de conversation 1-to-1 par recherche de membre
//   • Création de groupe avec 2+ destinataires + titre
//   • Badge compteur de non-lus sur le bouton flottant
//   • Mise à jour du `lastSeen` côté delegate (heartbeat) pour la notif mail
//     offline déclenchée par une CF cron (notifierChatOffline)
//
// Trois vues internes (state `view`):
//   "list"   → liste des conversations + bouton "nouvelle conversation"
//   "thread" → fil d'une conversation ouverte (state `openConvId`)
//   "new"    → recherche + sélection de destinataires
// Lot L11t (refonte « Mosaïque vivante ») : têtes NEUTRES (plus de bandeau en
// dégradé), avatars en tuiles (22 %, jamais un rond), survols en CSS
// (panneauFlottant.css, pointeur fin seulement), bulles lisibles dans les deux
// thèmes (plus de fond blanc en dur sous l'encre du thème), champs à 16 px au
// doigt, compteurs sans blanc sur orange. Aucun flux de données modifié.
// ============================================================================
import { useState, useEffect, useRef, useMemo } from "react";
import { mesurerEcriture } from "./mesureEcritures";
import { useChat, useChatMessages } from "./useFirebaseState";
import { toast } from "./uxComponents";
import { alpha } from "./alpha";
import { C } from "./couleurs";
import { Icone, IconeTexte, Tuile } from "./icones";
import { encreSur, encreSurBlanc } from "./navConstants";
import "./panneauFlottant.css";


// Position visuelle : à gauche du bouton Socrate (qui occupe right:20).
// On laisse 80px de marge entre les deux pour qu'ils cohabitent.
const POS = { bottom: "var(--bas-flottant, 70px)", right: 90 };
// Au DOIGT, le panneau ouvert ne peut pas garder l'ancrage de bureau : à
// right:90 avec 380px de large, son bord gauche tombe à −40px sur un écran de
// 430 et il est rogné. Sous pointeur grossier il s'étale donc d'un bord à
// l'autre. Le bouton, lui, ne bouge pas. Ces variables valent leur valeur de
// bureau hors tactile (cf. SOCLE MOBILE d'App.jsx) : rien ne change à la souris.
const PANNEAU_MOBILE = {
  left: "var(--chat-bord, auto)", right: "var(--chat-bord, 90px)",
  width: "var(--chat-largeur, 380px)",
};
// Sans bulle (lot L6) : le panneau s'ouvre SOUS l'en-tête, contre le bord droit
// (c'est de là, bouton « Messagerie », qu'on l'appelle) ; au doigt, --chat-bord
// l'étale toujours d'un bord à l'autre.
const POS_ENTETE = { top: "calc(var(--app-header-h, 56px) + 8px)", bottom: "auto", right: "var(--chat-bord, 12px)" };

// ── Format temps relatif court (FR) ────────────────────────────────────────
function timeAgo(ts) {
  if (!ts) return "";
  const diff = Date.now() - ts;
  const min = Math.floor(diff / 60000);
  if (min < 1) return "à l'instant";
  if (min < 60) return `il y a ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `il y a ${h} h`;
  const j = Math.floor(h / 24);
  if (j < 7) return `il y a ${j} j`;
  return new Date(ts).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" });
}

// ── Format date pour séparateur dans le thread ─────────────────────────────
function dateLabel(ts) {
  const d = new Date(ts);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dt = new Date(ts);
  dt.setHours(0, 0, 0, 0);
  const diffJ = Math.floor((today - dt) / 86400000);
  if (diffJ === 0) return "Aujourd'hui";
  if (diffJ === 1) return "Hier";
  return d.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });
}

// ── Titre d'une conversation (1-to-1 : autre membre, groupe : title ou liste) ─
function convTitle(conv, currentUserId) {
  if (!conv) return "Conversation";
  if (conv.type === "group") {
    if (conv.title) return conv.title;
    // Fallback : liste des prénoms (max 3)
    const others = (conv.members || [])
      .filter(id => id !== currentUserId)
      .slice(0, 3)
      .map(id => conv.membersInfo?.[id]?.prenom || "?");
    return others.join(", ") + (conv.members.length > 4 ? "…" : "");
  }
  // Direct : nom de l'autre
  const otherId = (conv.members || []).find(id => id !== currentUserId);
  const info = conv.membersInfo?.[otherId];
  if (info) return ((info.prenom || "") + " " + (info.nom || "")).trim();
  return "Membre";
}

function convSubtitle(conv, currentUserId) {
  if (!conv) return "";
  if (conv.type === "group") {
    return (conv.members?.length || 0) + " membres";
  }
  const otherId = (conv.members || []).find(id => id !== currentUserId);
  return conv.membersInfo?.[otherId]?.mutuelle || "";
}

// ── Vue THREAD : un fil de messages ────────────────────────────────────────
function ChatThread({ conv, currentUser, onBack, onCloseAll, sendMessage, markAsRead }) {
  const { messages, loading } = useChatMessages(conv?.id);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  // Marquer comme lu à l'ouverture + chaque nouveau message reçu
  useEffect(() => {
    if (conv?.id) markAsRead(conv.id);
  }, [conv?.id, messages.length, markAsRead]);

  // Auto-scroll en bas à chaque nouveau message
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTo(0, scrollRef.current.scrollHeight);
  }, [messages.length]);

  const handleSend = async () => {
    if (!text.trim() || sending) return;
    setSending(true);
    try { await sendMessage(conv.id, text, currentUser); setText(""); }
    catch (e) { console.warn("[chat] send error:", e.message); }
    setSending(false);
  };

  // Groupage par jour pour les séparateurs
  const grouped = useMemo(() => {
    const out = [];
    let lastDay = null;
    for (const m of messages) {
      const day = new Date(m.ts || 0); day.setHours(0,0,0,0);
      const key = day.getTime();
      if (key !== lastDay) {
        out.push({ type: "sep", label: dateLabel(m.ts), key: "sep-" + key });
        lastDay = key;
      }
      out.push({ type: "msg", ...m, key: "m-" + m.id });
    }
    return out;
  }, [messages]);

  return (
    <>
      {/* Header */}
      <div className="pf-tete">
        <button type="button" className="pf-icone" onClick={onBack} title="Retour à la liste" aria-label="Retour à la liste">
          <Icone name="chevron-left" size={18}/>
        </button>
        <div style={{flex:1,minWidth:0}}>
          <p className="pf-tete-t">{convTitle(conv, currentUser.id)}</p>
          <p className="pf-tete-s">
            {convSubtitle(conv, currentUser.id)}
            {conv?.type === "group" && " · groupe"}
          </p>
        </div>
        <button type="button" className="pf-icone" onClick={onCloseAll} title="Fermer la messagerie" aria-label="Fermer la messagerie">
          <Icone name="x" size={16}/>
        </button>
      </div>
      {/* Messages */}
      <div ref={scrollRef} style={{flex:1,overflowY:"auto",padding:"12px 12px",minHeight:200,
        background:"var(--bg-app)"}}>
        {loading && <div style={{textAlign:"center",fontSize:"var(--fs-micro)",color:"var(--text-sub)",padding:20}}>Chargement…</div>}
        {!loading && messages.length === 0 && (
          <div style={{textAlign:"center",padding:30,color:"var(--text-sub)",fontSize:"var(--fs-micro)",fontStyle:"italic"}}>
            Aucun message pour l'instant : envoyez le premier !
          </div>
        )}
        {grouped.map(item => {
          if (item.type === "sep") {
            return (
              <div key={item.key} style={{textAlign:"center",margin:"10px 0",fontSize:"var(--fs-micro)",
                color:"var(--text-sub)",fontWeight:600,textTransform:"capitalize"}}>
                – {item.label} –
              </div>
            );
          }
          const isMe = item.auteurId === currentUser.id;
          return (
            <div key={item.key} style={{marginBottom:8,display:"flex",flexDirection:"column",
              alignItems:isMe?"flex-end":"flex-start"}}>
              {!isMe && conv?.type === "group" && (
                <div style={{fontSize:"var(--fs-micro)",color:"var(--text-sub)",marginBottom:2,marginLeft:6,
                  fontWeight:700}}>{item.auteurNom}</div>
              )}
              <div style={{maxWidth:"82%",padding:"7px 11px",
                borderRadius:isMe?"12px 12px 2px 12px":"12px 12px 12px 2px",
                background:isMe?encreSurBlanc(C.teal):"var(--bg-card)",
                color:isMe?"#fff":"var(--text-main)",
                fontSize:"var(--fs-micro)",lineHeight:1.5,whiteSpace:"pre-wrap",wordBreak:"break-word",
                boxShadow:isMe?"none":"0 1px 2px rgba(0,0,0,.06)"}}>
                {item.text}
              </div>
              <div style={{fontSize:"var(--fs-micro)",color:"var(--text-sub)",marginTop:2,marginLeft:4,marginRight:4}}>
                {timeAgo(item.ts)}
              </div>
            </div>
          );
        })}
      </div>
      {/* Input */}
      <div style={{padding:"var(--filet-ecart) 10px",borderTop:"1px solid var(--border-color)",
        display:"flex",gap:6,background:"var(--bg-card,#fff)"}}>
        <textarea className="inp" rows={1} aria-label="Votre message"
          style={{flex:1,fontSize: "max(var(--fs-micro), var(--fs-champ-min))",padding:"8px 10px",resize:"none",maxHeight:80}}
          value={text} onChange={e => setText(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
          placeholder="Écrire un message… (Entrée pour envoyer, Maj+Entrée pour saut de ligne)"/>
        <button type="button" className="btn btn-orange" onClick={handleSend} disabled={!text.trim() || sending}
          title="Envoyer" aria-label="Envoyer" style={{flexShrink:0,padding:"8px 12px"}}>
          <Icone name="send" size={16}/>
        </button>
      </div>
    </>
  );
}

// ── Vue NEW : recherche + sélection destinataires ──────────────────────────
function NewConversation({ currentUser, delegates, onCancel, onCloseAll, onCreate }) {
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState([]); // array of delegate objects
  const [groupTitle, setGroupTitle] = useState("");
  const [creating, setCreating] = useState(false);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (q.length < 2) return [];
    return (delegates || [])
      .filter(d => d.actif !== false && d.supprime !== true && d.id !== currentUser?.id)
      .filter(d => !selected.find(s => s.id === d.id))
      .filter(d => {
        const fn = ((d.prenom||"") + " " + (d.nom||"")).toLowerCase();
        return fn.includes(q) || (d.mutuelle||"").toLowerCase().includes(q);
      })
      .slice(0, 8);
  }, [search, delegates, selected, currentUser?.id]);

  const isGroup = selected.length >= 2;
  const canCreate = selected.length >= 1 && (!isGroup || groupTitle.trim().length > 0);

  const handleCreate = async () => {
    if (!canCreate || creating) return;
    setCreating(true);
    try {
      await onCreate({
        type: isGroup ? "group" : "direct",
        members: selected,
        title: isGroup ? groupTitle.trim() : null,
      });
    } catch (e) {
      console.error("[chat] create conversation error:", e);
      toast.error("Échec de la création de la conversation. Réessayez.");
      setCreating(false);
    }
  };

  return (
    <>
      <div className="pf-tete">
        <button type="button" className="pf-icone" onClick={onCancel} title="Annuler" aria-label="Annuler">
          <Icone name="chevron-left" size={18}/>
        </button>
        <div style={{flex:1,minWidth:0}}>
          <p className="pf-tete-t">Nouvelle conversation</p>
          <p className="pf-tete-s">
            {selected.length === 0
              ? "Cherchez un ou plusieurs membres"
              : selected.length === 1
                ? "1 membre · conversation directe"
                : selected.length + " membres · groupe"}
          </p>
        </div>
        <button type="button" className="pf-icone" onClick={onCloseAll} title="Fermer la messagerie" aria-label="Fermer la messagerie">
          <Icone name="x" size={16}/>
        </button>
      </div>
      <div style={{flex:1,overflowY:"auto",padding:"12px",background:"var(--bg-card,#fff)"}}>
        <input className="inp" autoFocus value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Rechercher un membre…" aria-label="Rechercher un membre"
          style={{marginBottom:8,fontSize:"max(var(--fs-micro), var(--fs-champ-min))"}}/>
        {selected.length > 0 && (
          <div style={{display:"flex",gap:4,flexWrap:"wrap",marginBottom:10,
            padding:"6px 8px",background:"var(--card-inner,rgba(0,0,0,.03))",borderRadius:"var(--radius-sm)"}}>
            {selected.map(d => (
              <span key={d.id} style={{fontSize:"var(--fs-micro)",padding:"3px 8px",borderRadius:"var(--radius-lg)",
                background:alpha(C.teal, .133),color:"var(--tx-teal)",fontWeight:600,
                display:"flex",alignItems:"center",gap:4}}>
                {d.prenom} {d.nom}
                <button type="button" onClick={() => setSelected(s => s.filter(x => x.id !== d.id))}
                  aria-label={`Retirer ${d.prenom||""} ${d.nom||""}`.trim()}
                  style={{background:"none",border:"none",cursor:"pointer",display:"inline-flex",
                    color:"var(--tx-teal)",padding:0,lineHeight:1}}><Icone name="x" size={12}/></button>
              </span>
            ))}
          </div>
        )}
        {isGroup && (
          <div style={{marginBottom:10}}>
            <label style={{fontSize:"var(--fs-micro)",fontWeight:700,color:"var(--text-sub)",
              display:"block",marginBottom:3}}>Titre du groupe *</label>
            <input className="inp" value={groupTitle}
              onChange={e => setGroupTitle(e.target.value)}
              placeholder="Ex : Pôle Risques, Réunion projet X…"
              style={{fontSize:"max(var(--fs-micro), var(--fs-champ-min))"}}/>
          </div>
        )}
        {filtered.length > 0 && (
          <div style={{borderTop:"1px solid var(--border-color)",paddingTop:"var(--filet-ecart)"}}>
            {filtered.map(d => (
              <button key={d.id} type="button" className="pf-ligne"
                onClick={() => { setSelected(s => [...s, d]); setSearch(""); }}
                style={{display:"flex",alignItems:"center",gap:8,width:"100%",
                  padding:"7px 8px",border:"none",cursor:"pointer",
                  borderRadius:"var(--radius-sm)",textAlign:"left",marginBottom:2}}>
                <Tuile couleur={C.navy} taille={30}>
                  <span style={{fontSize:"var(--fs-micro)",fontWeight:600,lineHeight:1}}>{(d.prenom||"?")[0]}{(d.nom||"")[0]}</span>
                </Tuile>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:"var(--fs-micro)",fontWeight:600,color:"var(--text-main)"}}>
                    {d.prenom} {d.nom}
                  </div>
                  <div style={{fontSize:"var(--fs-micro)",color:"var(--text-sub)"}}>{d.mutuelle}</div>
                </div>
              </button>
            ))}
          </div>
        )}
        {search.trim().length >= 2 && filtered.length === 0 && (
          <div style={{textAlign:"center",padding:14,color:"var(--text-sub)",fontSize:"var(--fs-micro)",fontStyle:"italic"}}>
            Aucun membre trouvé.
          </div>
        )}
      </div>
      <div style={{padding:"var(--filet-ecart) 12px",borderTop:"1px solid var(--border-color)",
        background:"var(--bg-card,#fff)"}}>
        <button type="button" className={canCreate ? "btn btn-orange" : "btn btn-ghost"} onClick={handleCreate} disabled={!canCreate || creating}
          style={{width:"100%"}}>
          {creating ? "Création…"
            : isGroup ? "Créer le groupe"
            : selected.length === 1 ? "Démarrer la conversation"
            : "Choisissez au moins un destinataire"}
        </button>
      </div>
    </>
  );
}

// ── Vue LIST : liste des conversations ─────────────────────────────────────
function ConversationList({ conversations, currentUserId, onOpenConv, onNewConv, onCloseAll, loading }) {
  return (
    <>
      <div className="pf-tete">
        <Tuile couleur={C.teal} taille={34} icone="messages"/>
        <div style={{flex:1,minWidth:0}}>
          <p className="pf-tete-t">Messagerie</p>
          <p className="pf-tete-s">
            {conversations.length} conversation{conversations.length>1?"s":""}
          </p>
        </div>
        <button type="button" className="pf-icone" onClick={onNewConv} title="Nouvelle conversation" aria-label="Nouvelle conversation">
          <Icone name="plus" size={18}/>
        </button>
        <button type="button" className="pf-icone" onClick={onCloseAll} title="Fermer la messagerie" aria-label="Fermer la messagerie">
          <Icone name="x" size={16}/>
        </button>
      </div>
      <div style={{flex:1,overflowY:"auto",background:"var(--bg-card,#fff)"}}>
        {loading && <div style={{textAlign:"center",padding:30,color:"var(--text-sub)",fontSize:"var(--fs-micro)"}}>Chargement…</div>}
        {!loading && conversations.length === 0 && (
          <div style={{textAlign:"center",padding:"40px 20px",color:"var(--text-sub)",fontSize:"var(--fs-micro)",lineHeight:1.6}}>
            <div style={{fontSize:"var(--fs-page)",marginBottom:10}}><IconeTexte nom="messages" titre="Messages"/></div>
            <div>Aucune conversation pour le moment.</div>
            <div style={{fontSize:"var(--fs-micro)",marginTop:6,fontStyle:"italic"}}>
              Touchez <strong>Nouvelle conversation</strong> (le signe plus, en haut) pour écrire à un membre.
            </div>
          </div>
        )}
        {conversations.map(c => {
          const unread = (c.unread || {})[currentUserId] || 0;
          return (
            <button key={c.id} type="button" className="pf-ligne" onClick={() => onOpenConv(c.id)}
              style={{display:"flex",alignItems:"center",gap:10,width:"100%",
                padding:"var(--filet-ecart) 12px",border:"none",cursor:"pointer",
                borderBottom:"1px solid var(--border-color)",textAlign:"left",
                position:"relative"}}>
              {c.type === "group"
                ? <Tuile couleur={C.orange} taille={38} icone="users"/>
                : <Tuile couleur={C.teal} taille={38}>
                    <span style={{fontSize:"var(--fs-nav)",fontWeight:600,lineHeight:1}}>{(() => {
                      const otherId = (c.members||[]).find(id => id !== currentUserId);
                      const info = c.membersInfo?.[otherId];
                      return ((info?.prenom||"?")[0] + (info?.nom||"")[0]).toUpperCase();
                    })()}</span>
                  </Tuile>}
              <div style={{flex:1,minWidth:0}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:6}}>
                  <div style={{fontSize:"var(--fs-micro)",fontWeight:600,color:"var(--text-main)",
                    whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
                    {convTitle(c, currentUserId)}
                  </div>
                  <div style={{fontSize:"var(--fs-micro)",color:"var(--text-sub)",flexShrink:0}}>
                    {c.lastMessageAt ? timeAgo(c.lastMessageAt) : ""}
                  </div>
                </div>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:6,marginTop:2}}>
                  <div style={{fontSize:"var(--fs-micro)",color:unread>0?"var(--text-main)":"var(--text-sub)",
                    fontWeight:unread>0?700:400,
                    whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",flex:1}}>
                    {c.lastMessage
                      ? (c.lastMessage.auteurId === currentUserId ? "Vous : " : "")
                        + (c.lastMessage.text || "")
                      : <em style={{color:"var(--text-sub)"}}>Conversation créée</em>}
                  </div>
                  {unread > 0 && (
                    <span style={{background:C.orange,color:encreSur(C.orange),fontWeight:700,
                      fontSize:"var(--fs-micro)",padding:"1px 7px",borderRadius:"var(--radius-md)",flexShrink:0}}>
                      {unread > 99 ? "99+" : unread}
                    </span>
                  )}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </>
  );
}

// ── Composant principal exporté ────────────────────────────────────────────
// Lot L6 (décision de Pierre n° 8) : la coquille PILOTE la messagerie —
// `ouvert` / `onOuvertChange` (bouton « Messagerie » de l'en-tête, « Moi »,
// palette), `onNonLus` (compteur remonté à l'en-tête et à l'onglet « Moi »),
// `sansBulle` (plus de bulle flottante : le panneau s'ouvre sous l'en-tête).
// Sans ces props, le composant garde son fonctionnement d'origine.
export default function FloatingChat({ aramisSession, delegates, openConvIdRequest, ouvert, onOuvertChange, onNonLus, sansBulle = false }) {
  const currentUserId = aramisSession?.id || null;
  const { conversations, sendMessage, createConversation, markAsRead, totalUnread, loading }
    = useChat(currentUserId);

  const [ouvertLocal, setOuvertLocal] = useState(false);
  const pilote = typeof ouvert === "boolean";
  const open = pilote ? ouvert : ouvertLocal;
  const setOpen = (v) => {
    const suivant = !!(typeof v === "function" ? v(open) : v);
    if (pilote) { if (onOuvertChange) onOuvertChange(suivant); } else setOuvertLocal(suivant);
  };
  // Le compteur des messages non lus remonte à la coquille.
  useEffect(() => { if (onNonLus) onNonLus(totalUnread || 0); }, [totalUnread]); // eslint-disable-line react-hooks/exhaustive-deps
  const [view, setView] = useState("list"); // "list" | "thread" | "new"
  const [openConvId, setOpenConvId] = useState(null);

  // Ouvrir automatiquement une conversation si demandée par deep link
  useEffect(() => {
    if (openConvIdRequest) {
      setOpenConvId(openConvIdRequest);
      setView("thread");
      setOpen(true);
    }
  }, [openConvIdRequest]); // eslint-disable-line react-hooks/exhaustive-deps

  const openConv = conversations.find(c => c.id === openConvId) || null;

  const currentUser = useMemo(() => ({
    id: aramisSession?.id,
    prenom: aramisSession?.prenom || "",
    nom: aramisSession?.nom || "",
    mutuelle: aramisSession?.mutuelle || "",
  }), [aramisSession]);

  // Heartbeat lastSeen — mis à jour à l'ouverture du widget et toutes les
  // 5 min tant qu'il est ouvert. Utilisé par la CF cron pour décider d'envoyer
  // un mail offline aux destinataires inactifs.
  useEffect(() => {
    if (!open || !currentUserId) return;
    const update = async () => {
      try {
        const { updateDoc, doc } = await import("firebase/firestore");
        const { db } = await import("./firebase");
        mesurerEcriture("lastSeen");
        await updateDoc(doc(db, "delegates", currentUserId), { lastSeen: Date.now() });
      } catch (e) { /* silencieux */ }
    };
    update();
    const i = setInterval(update, 5 * 60 * 1000);
    return () => clearInterval(i);
  }, [open, currentUserId]);

  if (!currentUserId) return null;

  // Bouton compact (chat fermé)
  if (!open) {
    if (sansBulle) return null;
    return (
      <button onClick={() => setOpen(true)} data-bulle-messagerie=""
        title={`Messagerie${totalUnread>0?`, ${totalUnread} non lu${totalUnread>1?"s":""}`:""}`}
        type="button" className="pf-bulle"
        style={{position:"fixed",bottom:POS.bottom,right:POS.right,zIndex:9989,
          width:56,height:56,borderRadius:"50%",
          background:"var(--tuile-navy)",
          border:"none",cursor:"pointer",boxShadow:"var(--elev-2)",
          display:"flex",alignItems:"center",justifyContent:"center",
          fontSize:"var(--fs-section)",color:"var(--tuile-navy-glyphe)"}}>
        <IconeTexte nom="messages"/>
        {totalUnread > 0 && (
          <span style={{position:"absolute",top:-4,right:-4,background:C.orange,
            color:encreSur(C.orange),fontSize:"var(--fs-micro)",fontWeight:700,padding:"2px 6px",borderRadius:"var(--radius-md)",
            border:"2px solid var(--bg-app)",minWidth:18,textAlign:"center"}}>
            {totalUnread > 99 ? "99+" : totalUnread}
          </span>
        )}
      </button>
    );
  }

  // Panneau ouvert
  return (
    <div style={{position:"fixed",bottom:POS.bottom,zIndex:9989,
      ...PANNEAU_MOBILE, ...(sansBulle ? POS_ENTETE : null), maxWidth:"calc(var(--vw100) - 20px)",
      borderRadius:"var(--radius-lg)",overflow:"hidden",boxShadow:"0 8px 40px rgba(0,0,0,.3)",
      border:"1px solid var(--border-color)",
      // Jamais plus haut que l'espace sous l'en-tête : en paysage et en « Très
      // grand », 70 % de l'écran plus l'en-tête agrandi dépassaient le bas.
      display:"flex",flexDirection:"column",height:"min(var(--vh100) * 0.7, 580px, calc(var(--vh100) - var(--app-header-h, 56px) - 16px))",
      background:"var(--bg-card,#fff)"}}>
      {view === "list" && (
        <ConversationList
          conversations={conversations}
          currentUserId={currentUserId}
          loading={loading}
          onOpenConv={(id) => { setOpenConvId(id); setView("thread"); }}
          onNewConv={() => setView("new")}
          onCloseAll={() => setOpen(false)}/>
      )}
      {view === "thread" && openConv && (
        <ChatThread
          conv={openConv}
          currentUser={currentUser}
          sendMessage={sendMessage}
          markAsRead={markAsRead}
          onBack={() => { setView("list"); setOpenConvId(null); }}
          onCloseAll={() => setOpen(false)}/>
      )}
      {view === "new" && (
        <NewConversation
          currentUser={currentUser}
          delegates={delegates}
          onCancel={() => setView("list")}
          onCloseAll={() => setOpen(false)}
          onCreate={async (payload) => {
            const conv = await createConversation({ ...payload, currentUser });
            setOpenConvId(conv.id);
            setView("thread");
          }}/>
      )}
    </div>
  );
}
